<template>
    <vx-chart-record
        :loading="loading"
        :error="Boolean(error)"
        :percentage="true"
        :data="dataComputed || []"
        @item:click="($event, item) => emit('resource:click', $event, item)"
    >
        <template v-slot:title>
            <slot name="title"> {{ resource.title }} per Resource </slot>
            &rarr; Top {{ dataComputed?.length || 10 }}
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <template v-slot:item-title="{ item }">
            <campaign-details-popover
                v-if="item.resource_type === 'campaign'"
                class="vx-ml-1 [&>div:first-child]:vx-truncate vx-w-full"
                :modelValue="{ id: item.resource_id, name: item.resource_name }"
                nameIcon
            >
                {{ item.title }}
            </campaign-details-popover>
            <vx-popover v-else hover placement="top" arrow class="[&>div:first-child]:vx-truncate vx-w-full">
                {{ item.title }}
                <template v-slot:content>
                    <div>
                        <font-awesome-icon
                            :icon="resourceTypeIcon(item.resource_type)"
                            class="vx-mr-2 vx-text-slate-500"
                        ></font-awesome-icon>
                        <span class="vx-font-bold vx-text-slate-700 vx-text-lg vx-break-all">{{ item.title }}</span>
                        <vx-badge size="xs" color="slate" class="vx-ml-2">{{ item.resource_id }}</vx-badge>
                    </div>
                </template>
            </vx-popover>
        </template>

        <template v-slot:explainHeader>{{ resource.title }} per Resource</template>
        <template v-slot:explainContent>
            <p>
                <strong>{{ resource.explainTitle }}</strong
                ><br />
                {{ resource.explainContent }}
            </p>
        </template>
    </vx-chart-record>
</template>

<script setup>
import { VxChartRecord, VxPopover, VxBadge } from '@voxie/frontend-components';
import { analyticsClient } from '~/services';
import { watch } from 'vue';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { computed } from 'vue';
import { metricsInputValidator, resourceTypeIcon } from '~/utils/analytics';
import { stringifyQueryString } from '~/utils/string';
import CampaignDetailsPopover from '~/components/campaigns/campaign-card/CampaignDetailsPopover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    rateType: {
        type: String,
        validator: (resourceType) => ['click-through', 'message-reply', 'opt-out'].includes(resourceType),
        required: true,
    },
});

const emit = defineEmits(['resource:click']);

const teamId = Spark.state.currentTeam.id;

const resource = computed(() => {
    switch (props.rateType) {
        case 'click-through':
            return {
                title: 'Click-Through Rate',
                explainTitle: 'Click-Through Rate',
                explainContent:
                    'Most relevant Voxie Resources, in particular the ones that provide a clickable link; this metric allows you to evaluate the success through the relative clicks a Message Resource receives relative to the quantity of messages delivered.',
                resourceDataRequest: analyticsClient.outboundMessagesClickThroughRateByResource(
                    teamId,
                    props.fetchParams
                ),
            };
        case 'message-reply':
            return {
                title: 'Message Replies',
                explainTitle: 'Message Replies',
                explainContent:
                    'Most relevant Voxie Resources in terms of reply percentage of the messages delivered relative to the messages delivered during the selected period.',
                resourceDataRequest: analyticsClient.outboundMessagesReplyRateByResource(teamId, props.fetchParams),
            };
        case 'opt-out':
            return {
                title: 'Unsubscribe',
                explainTitle: 'Unsubscribers %',
                explainContent:
                    'Most relevant Voxie Resources in terms of opt-out percentage relative to the messages delivered during the selected period.',
                resourceDataRequest: analyticsClient.outboundMessagesOptOutRateByResource(teamId, props.fetchParams),
            };
        default:
            return {
                title: 'Unknown',
                explainTitle: 'Unknown',
                explainContent: 'unknown',
                resourceDataRequest: async () => ({ data: { data: [] } }),
            };
    }
});

const { loading, response, error, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    return (response.value?.data?.data || []).map((item) => {
        const queryString = {
            ...props.fetchParams,
            filters: {
                ...props.fetchParams.filters,
                resources: [
                    {
                        resource_type: item.resource_type,
                        resource_id: item.resource_id,
                    },
                ],
            },
        };
        return {
            ...item,
            href: '/analytics/outbound-messages?' + stringifyQueryString(queryString),
            key: item.resource_id,
            title: item.resource_name,
            value: item.values[0],
        };
    });
});

watch(
    () => props.fetchParams,
    () => {
        fetchData(resource.value.resourceDataRequest).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);
</script>
